import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI, chariowAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Coins,
  Zap,
  Crown,
  Sparkles,
  Brain,
  FileText,
  CreditCard,
  ArrowRight,
  Check,
  Star,
  TrendingUp,
  RefreshCw,
  ExternalLink,
  Loader2,
  Clock,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Credits = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseReference, setPurchaseReference] = useState(null);
  const [pollingInterval, setPollingIntervalState] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('dockhta_user') || '{}');

  useEffect(() => {
    loadData();
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, packsRes, plansRes, transRes] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getPacks(),
        creditsAPI.getPlans(),
        creditsAPI.getTransactions()
      ]);
      setBalance(balanceRes.data);
      setPacks(packsRes.data.packs);
      setPlans(plansRes.data.plans);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      console.error('Error loading credits data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Start polling for purchase confirmation
  const startPurchasePolling = useCallback((reference) => {
    const interval = setInterval(async () => {
      try {
        const response = await creditsAPI.checkPurchaseStatus(reference);
        if (response.data.status === 'confirmed') {
          clearInterval(interval);
          setPollingIntervalState(null);
          setPurchaseReference(null);
          toast.success(`${response.data.credits} crédits ajoutés à votre compte !`);
          loadData(); // Refresh balance
        }
      } catch (error) {
        console.error('Error checking purchase status:', error);
      }
    }, 5000);
    setPollingIntervalState(interval);
  }, []);

  const handlePurchase = async (packKey) => {
    setPurchasing(true);
    try {
      const response = await creditsAPI.purchase({ pack: packKey });
      const { reference, chariow_url } = response.data;
      
      setPurchaseReference(reference);
      toast.info('Redirection vers le paiement...');
      
      // Open Chariow
      window.open(chariow_url, '_blank');
      
      // Start polling
      startPurchasePolling(reference);
      
    } catch (error) {
      console.error('Error initiating purchase:', error);
      toast.error('Erreur lors de la création de la commande');
    } finally {
      setPurchasing(false);
    }
  };

  const getPackIcon = (key) => {
    const icons = {
      decouverte: Zap,
      standard: Star,
      pro: Crown,
      elite: Sparkles
    };
    return icons[key] || Coins;
  };

  const getPackColor = (key) => {
    const colors = {
      decouverte: 'from-blue-500 to-cyan-500',
      standard: 'from-purple-500 to-pink-500',
      pro: 'from-amber-500 to-orange-500',
      elite: 'from-emerald-500 to-teal-500'
    };
    return colors[key] || 'from-gray-500 to-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header with Balance */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Mes Crédits</h1>
            <p className="text-slate-500 mt-1">Gérez vos crédits et abonnements</p>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-[#1976D2] to-[#7C3AED] text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Solde actuel</p>
                <p className="text-4xl font-bold">{balance?.balance || 0}</p>
                <p className="text-white/70 text-sm mt-1">crédits disponibles</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Coins className="w-8 h-8" />
              </div>
            </div>
            
            {/* Free tier info */}
            {balance?.ai_questions_free > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/70">Questions IA gratuites ce mois</span>
                  <span className="font-medium">
                    {balance.ai_questions_remaining_free === -1 
                      ? 'Illimitées' 
                      : `${balance.ai_questions_remaining_free} / ${balance.ai_questions_free}`
                    }
                  </span>
                </div>
                {balance.ai_questions_free !== -1 && (
                  <Progress 
                    value={(balance.ai_questions_remaining_free / balance.ai_questions_free) * 100} 
                    className="h-2 bg-white/20"
                  />
                )}
              </div>
            )}

            {/* Subscription status */}
            {balance?.subscription !== 'gratuit' && (
              <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5" />
                  <span className="font-medium capitalize">Abonnement {balance.subscription}</span>
                </div>
                {balance.subscription_expires && (
                  <span className="text-sm text-white/70">
                    Expire le {format(new Date(balance.subscription_expires), 'dd MMM yyyy', { locale: fr })}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Polling indicator */}
      {purchaseReference && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="p-4 flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900">Vérification du paiement en cours...</p>
              <p className="text-xs text-purple-700">Référence: {purchaseReference}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const pack = packs.find(p => p.popular) || packs[0];
                window.open(pack.url + `?ref=${purchaseReference}`, '_blank');
              }}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Ouvrir Chariow
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="packs" className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="packs">Acheter</TabsTrigger>
          <TabsTrigger value="plans">Abonnements</TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        {/* Credit Packs */}
        <TabsContent value="packs" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {packs.map((pack) => {
              const Icon = getPackIcon(pack.key);
              const gradient = getPackColor(pack.key);
              
              return (
                <Card 
                  key={pack.key} 
                  className={`relative overflow-hidden hover:shadow-lg transition-all ${pack.popular ? 'ring-2 ring-purple-500' : ''}`}
                >
                  {pack.popular && (
                    <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Populaire
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{pack.name}</CardTitle>
                    <CardDescription className="text-xs">{pack.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-3xl font-bold text-slate-900">{pack.credits}</span>
                      <span className="text-slate-500 text-sm">crédits</span>
                    </div>
                    <p className="text-lg font-semibold text-[#E53935]">
                      {pack.price_fcfa.toLocaleString()} FCFA
                    </p>
                    <p className="text-xs text-slate-500">
                      {pack.price_per_credit} FCFA/crédit
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      onClick={() => handlePurchase(pack.key)}
                      disabled={purchasing}
                      className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90`}
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Acheter <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* Usage Info */}
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-500" />
                  Questions IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">1 crédit</p>
                <p className="text-xs text-slate-500">par question</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Documents médicaux
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">5 crédits</p>
                <p className="text-xs text-slate-500">par document (au-delà de 10 gratuits)</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscription Plans */}
        <TabsContent value="plans" className="mt-6">
          {user.role === 'patient' ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <Gift className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Compte Patient Gratuit</h3>
                <p className="text-slate-500 mb-4">
                  Les patients bénéficient de 10 questions IA gratuites par mois et 10 documents médicaux gratuits.
                </p>
                <p className="text-sm text-slate-400">
                  Les crédits sont utilisés uniquement au-delà des quotas gratuits.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {plans && Object.entries(plans[user.role] || plans.professional).map(([key, plan]) => (
                <Card 
                  key={key} 
                  className={`relative ${balance?.subscription === key ? 'ring-2 ring-emerald-500' : ''} ${key === 'premium' || key === 'enterprise' ? 'border-purple-200' : ''}`}
                >
                  {balance?.subscription === key && (
                    <div className="absolute -top-3 left-4 bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                      Actuel
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="capitalize">{key}</CardTitle>
                    <CardDescription>
                      {plan.credits_per_month > 0 
                        ? `${plan.credits_per_month} crédits/mois`
                        : 'Gratuit'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  {key !== 'gratuit' && balance?.subscription !== key && (
                    <CardFooter className="flex-col gap-2">
                      <Button 
                        className="w-full"
                        onClick={() => {
                          // Check if user has enough credits
                          if (balance?.balance >= plan.credits_per_month) {
                            toast.info('Fonctionnalité bientôt disponible');
                          } else {
                            toast.error(`Vous avez besoin de ${plan.credits_per_month} crédits pour ce plan`);
                          }
                        }}
                      >
                        Mensuel ({plan.credits_per_month} crédits)
                      </Button>
                      <Button variant="outline" className="w-full text-xs">
                        Annuel ({plan.credits_per_month * 10} crédits - 2 mois offerts)
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Transaction History */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune transaction</p>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                          }`}>
                            {tx.type === 'purchase' ? (
                              <CreditCard className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            ) : tx.type === 'ai_usage' ? (
                              <Brain className="w-5 h-5 text-red-600" />
                            ) : (
                              <Coins className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(tx.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Credits;
